/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { isNonEmptyArray } from '@apollo/client/utilities';
import {
  Avatar,
  Button,
  CheckIcon,
  Combobox,
  Group,
  Input,
  Pill,
  PillsInput,
  Stack,
  Text,
  TextInput,
  rem,
  useCombobox,
  useMantineTheme,
} from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { modals } from '@mantine/modals';
import { isNil } from 'es-toolkit';
import { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { z } from 'zod';

import i18n from '@fai/friendli-suite/features/i18n';
import { useDedicatedMemberList } from '@fai/friendli-suite/features/products/dedicated-endpoints/use-dedicated-members';
import Paths from '@fai/friendli-suite/features/routes/paths';
import { useUserId } from '@fai/friendli-suite/features/settings/user-settings/use-user';
import type { SimpleUserWithEmail } from '@fai/friendli-suite/features/types/user-settings';
import { UserAvatarIcon } from '@fai/svgr';
import { TextVariants } from '@fai/ui/components/typography/variants';
import { FooterlessModalFooter } from '@fai/ui/mantine-modal/helpers';
import { InternalLinkWrapper } from '@fai/ui/views/common/link-wrapper';
import MessageBox from '@fai/ui/views/common/message-box';
import { MessageBoxVariant } from '@fai/ui/views/common/message-box/variants';
import { isTargetSearched } from '@fai/util';

import { useCreateProject } from './use-projects';

const setNameModalId = 'dedicated-endpoints-project-create-set-name-modal';
const addMembersModalId = 'dedicated-endpoints-project-create-add-members-modal';
const closeSetNameModal = () => modals.close(setNameModalId);
const closeAddMembersModal = () => modals.close(addMembersModalId);

interface SetNameModalBodyProps {
  initialName?: string | null;
}
interface AddMembersModalBodyProps {
  projectName: string;
}

const openSetNameModal = (props?: SetNameModalBodyProps) => {
  modals.openContextModal({
    modal: 'footerless',
    modalId: setNameModalId,
    title: i18n.t('dedicated_endpoints.project.create_a_new_project'),
    closeOnClickOutside: false,
    closeOnEscape: false,
    innerProps: { modalBody: <SetNameModalBody {...props} /> },
  });
};
const openAddMembersModal = (props: AddMembersModalBodyProps) => {
  modals.openContextModal({
    modal: 'footerless',
    modalId: addMembersModalId,
    title: i18n.t('dedicated_endpoints.project.add_members_to_project', {
      projectName: props.projectName,
    }),
    closeOnClickOutside: false,
    closeOnEscape: false,
    innerProps: { modalBody: <AddMembersModalBody {...props} /> },
  });
};

const setNameReqBodyValidateSchema = z.object({
  name: z.string().min(1).max(50),
});
const addMembersReqBodyValidateSchema = z.object({
  memberIds: z.array(z.string()),
});

function SetNameModalBody({ initialName }: SetNameModalBodyProps) {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const formContext = useForm({
    initialValues: { name: initialName ?? '' },
    validate: zodResolver(setNameReqBodyValidateSchema),
  });

  const projectName = formContext.getInputProps('name').value;
  const isNextButtonDisabled = !formContext.isValid();

  const onClickNextButton = () => {
    openAddMembersModal({ projectName });
    closeSetNameModal();
  };

  return (
    <form onReset={formContext.onReset} onSubmit={(e) => e.preventDefault()}>
      <Stack gap={theme.pf.layout.xs}>
        <Input.Wrapper>
          <Input.Label mb={theme.pf.layout['2xs']}>
            <Text c={theme.pf.color.fg.neutral.strong} variant={TextVariants.TitleSm}>
              {t('dedicated_endpoints.project.project_name')}
            </Text>
          </Input.Label>

          <TextInput
            maxLength={50}
            placeholder={`${t('dedicated_endpoints.project.enter_the_project_name')}`}
            rightSection={
              <Text
                c={theme.pf.color.fg.neutral.subtle}
                variant={TextVariants.BodyMd}
              >{`${projectName.length}/50`}</Text>
            }
            styles={{
              input: { paddingRight: rem(56) },
              section: {
                width: 'fit-content',
                margin: `0 ${theme.pf.layout['2xs']}`,
              },
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !isNextButtonDisabled) {
                onClickNextButton();
              }
            }}
            {...formContext.getInputProps('name')}
          />
        </Input.Wrapper>

        <FooterlessModalFooter>
          <Button theme="cloud" disabled={isNextButtonDisabled} onClick={onClickNextButton}>
            {t('shared.next')}
          </Button>
        </FooterlessModalFooter>
      </Stack>
    </form>
  );
}

function AddMembersModalBody({ projectName }: AddMembersModalBodyProps) {
  const { t } = useTranslation();
  const { teamId = '' } = useParams();
  const theme = useMantineTheme();

  const [search, setSearch] = useState('');
  const [targetMembers, setTargetMembers] = useState<SimpleUserWithEmail[]>([]);

  const formContext = useForm({
    initialValues: { memberIds: [] as string[] },
    validate: zodResolver(addMembersReqBodyValidateSchema),
  });

  const { data: userId } = useUserId();
  const { data: _memberList, loading: memberListLoading } = useDedicatedMemberList(teamId);
  const { createProject, loading: createProjectLoading } = useCreateProject({
    onCompleted: closeAddMembersModal,
  });

  const memberList = _memberList?.filter(({ id }) => id !== userId) ?? [];
  const filteredMemberList = memberList.filter(({ name, email }) =>
    isTargetSearched([name, email], search)
  );

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
    onDropdownOpen: () => combobox.updateSelectedOptionIndex('active'),
  });

  const selectMember = (target: SimpleUserWithEmail) => {
    const isIncludedInList = targetMembers.some(({ id }) => id === target.id);
    const newTargetMembers = isIncludedInList
      ? targetMembers.filter(({ id }) => id !== target.id)
      : [...targetMembers, target];

    if (!isIncludedInList) {
      setSearch('');
    }

    setTargetMembers(newTargetMembers);
    formContext.setFieldValue(
      'memberIds',
      newTargetMembers.map(({ id }) => id)
    );
  };

  const removeMemberFromInput = (target: SimpleUserWithEmail) => {
    const newTargetMembers = targetMembers.filter(({ id }) => id !== target.id);

    setTargetMembers(newTargetMembers);
    formContext.setFieldValue(
      'memberIds',
      newTargetMembers.map(({ id }) => id)
    );
  };

  const getMemberObject = (targetId: string) => {
    const targetMember = memberList.find(({ id }) => id === targetId);

    // We always can find targetMember
    if (isNil(targetMember)) {
      const cannotFindMemberError = new Error('Cannot find target member in add-members-modal');
      cannotFindMemberError.name = 'Assertion error';

      throw cannotFindMemberError;
    }

    return targetMember;
  };

  return (
    <form onReset={formContext.onReset} onSubmit={(e) => e.preventDefault()}>
      <Stack gap={theme.pf.layout.xs}>
        {!memberListLoading && memberList.length === 0 && (
          <MessageBox variant={MessageBoxVariant.InformativeCloud}>
            <Trans
              i18nKey="dedicated_endpoints.project.add_members_description"
              components={[
                <InternalLinkWrapper
                  icon={null}
                  url={Paths.team(teamId).teamSettings.members}
                  onClick={closeAddMembersModal}
                />,
              ]}
            />
          </MessageBox>
        )}

        <Combobox
          store={combobox}
          onOptionSubmit={(targetId) => selectMember(getMemberObject(targetId))}
        >
          <Combobox.DropdownTarget>
            <PillsInput
              styles={{ input: { padding: `${rem(7.5)} ${rem(12)}` } }}
              onClick={() => combobox.openDropdown()}
            >
              <Pill.Group>
                {targetMembers.map((member) => (
                  <InputMemberItem
                    key={member.id}
                    member={member}
                    onRemove={removeMemberFromInput}
                  />
                ))}
                <Combobox.EventsTarget>
                  <PillsInput.Field
                    placeholder="Natalie, or james@example.com"
                    value={search}
                    onBlur={() => combobox.closeDropdown()}
                    onChange={(event) => {
                      combobox.updateSelectedOptionIndex();
                      setSearch(event.currentTarget.value);
                    }}
                    onFocus={() => combobox.openDropdown()}
                    onKeyDown={(event) => {
                      if (event.key === 'Backspace' && search.length === 0) {
                        event.preventDefault();
                        removeMemberFromInput(targetMembers[targetMembers.length - 1]);
                      }
                    }}
                  />
                </Combobox.EventsTarget>
              </Pill.Group>
            </PillsInput>
          </Combobox.DropdownTarget>

          {search !== '' && (
            <Combobox.Dropdown
              mah={rem(180)}
              style={{
                boxShadow: theme.pf.shadow['shadow-md'],
                overflowY: 'auto',
              }}
            >
              <Combobox.Options>
                {isNonEmptyArray(filteredMemberList) ? (
                  filteredMemberList.map((member) => (
                    <OptionMemberItem
                      key={member.id}
                      active={targetMembers.some(({ id }) => id === member.id)}
                      member={member}
                    />
                  ))
                ) : (
                  <Combobox.Empty>Nobody found...</Combobox.Empty>
                )}
              </Combobox.Options>
            </Combobox.Dropdown>
          )}
        </Combobox>

        <FooterlessModalFooter justify="space-between">
          <Button
            variant="transparent"
            c={theme.pf.color.fg.neutral.subtle}
            miw="unset"
            onClick={() => {
              openSetNameModal({ initialName: projectName });
              closeAddMembersModal();
            }}
          >
            {t('shared.back')}
          </Button>

          <Group gap={theme.pf.layout.xs}>
            <Button
              variant="transparent"
              loading={createProjectLoading}
              onClick={() => {
                createProject({ name: projectName, memberIds: [] });
              }}
            >
              {t('dedicated_endpoints.project.skip_for_now')}
            </Button>

            <Button
              theme="cloud"
              loading={createProjectLoading}
              disabled={!formContext.isValid() || formContext.getValues().memberIds.length === 0}
              onClick={() => {
                createProject({
                  name: projectName,
                  memberIds: targetMembers.map(({ id }) => id),
                });
              }}
            >
              {t('shared.add')}
            </Button>
          </Group>
        </FooterlessModalFooter>
      </Stack>
    </form>
  );
}

function InputMemberItem({
  member,
  onRemove,
}: {
  member: SimpleUserWithEmail;
  onRemove: (id: SimpleUserWithEmail) => void;
}) {
  const theme = useMantineTheme();

  return (
    <Pill
      bd={`1px solid ${theme.pf.color.border.neutral.default}`}
      bg={theme.pf.color.bg.neutral.subtle.base}
      h={rem(24)}
      pl={theme.pf.layout['3xs']}
      withRemoveButton={true}
      style={{ borderRadius: theme.pf.radius.sm }}
      onRemove={() => onRemove(member)}
    >
      <Group gap={theme.pf.layout['4xs']} wrap="nowrap">
        <Avatar size={rem(16)} src={member.avatarUrl} styles={{ placeholder: { border: 'none' } }}>
          <UserAvatarIcon />
        </Avatar>

        <Text
          c={theme.pf.color.fg.neutral.strong}
          variant={TextVariants.BodyMd}
          style={{ whiteSpace: 'nowrap' }}
        >
          {member.name ?? '(name)'}
        </Text>
      </Group>
    </Pill>
  );
}

function OptionMemberItem({ member, active }: { member: SimpleUserWithEmail; active: boolean }) {
  const theme = useMantineTheme();

  return (
    <Combobox.Option active={active} value={member.id}>
      <Group gap={theme.pf.layout['3xs']}>
        {active && <CheckIcon size={12} />}

        <Avatar size={rem(16)} src={member.avatarUrl} styles={{ placeholder: { border: 'none' } }}>
          <UserAvatarIcon />
        </Avatar>

        <Text
          c={theme.pf.color.fg.neutral.strong}
          variant={TextVariants.BodyMd}
          style={{ whiteSpace: 'nowrap' }}
        >
          {member.name ?? '(name)'}
        </Text>
      </Group>
    </Combobox.Option>
  );
}

export const openCreateProjectModal = openSetNameModal;
